import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  PlusIcon,
  EyeIcon,
  ArrowTopRightOnSquareIcon,
  PlayIcon,
  PauseIcon,
  TrashIcon,
  PencilIcon,
  ServerIcon,
  CloudIcon,
  XMarkIcon,
  ArrowLeftIcon,
  CpuChipIcon,
  CircleStackIcon,
  GlobeAltIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  LinkIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  CogIcon,
  PuzzlePieceIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import {
  ServerIcon as ServerIconSolid,
  PlayIcon as PlayIconSolid,
  PauseIcon as PauseIconSolid
} from '@heroicons/react/24/solid';

// Design System Components
import Button from '../ui/Button';
import { SearchInput } from '../ui/Input';
import Card from '../ui/Card';
import { StatusBadge, ActionMenu } from '../ui/Table';
import { FormModal } from '../ui/Modal';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import axiosInstance from '../api/axiosInstance';
import tokenManager from '../../utils/tokenManager';
import logger from '../../utils/logger';

// Helper functions for instances
const getEnvironmentColor = (environment) => {
  const colors = {
    production: 'from-red-500 to-red-600',
    staging: 'from-yellow-500 to-yellow-600',
    development: 'from-green-500 to-green-600'
  };
  return colors[environment] || 'from-gray-500 to-gray-600';
};

const getInstanceUrl = (instance, { withToken = false } = {}) => {
  let url;
  // Development environment: use instance ID routing
  if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
    url = `http://localhost:3001/instance/${instance._id || instance.id}`;
  } else if (instance.fullUrl) {
    // Production: use subdomain
    url = `https://${instance.fullUrl}`;
  } else {
    // Fallback
    url = `https://${instance.subdomain}.tellency.com`;
  }

  // Append auth token for cross-domain SSO (tellency.com instances opened from aiinak.com admin)
  if (withToken) {
    const token = tokenManager.getToken();
    if (token) {
      const separator = url.includes('?') ? '&' : '?';
      url = `${url}${separator}authToken=${encodeURIComponent(token)}`;
    }
  }
  return url;
};

const getInstanceDisplayUrl = (instance) => {
  // Development: show instance ID routing format
  if (process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost') {
    return `localhost:3001/instance/${instance._id || instance.id}`;
  }
  
  // Production: show subdomain
  return `${instance.subdomain}.tellency.com`;
};

const getEnvironmentBadge = (environment) => {
  const badges = {
    production: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    staging: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    development: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  };
  return badges[environment] || 'bg-gray-100 text-gray-800 dark:bg-[#111111]/30 dark:text-gray-400';
};

const formatDistanceToNow = (date) => {
  const now = new Date();
  const past = new Date(date);
  const diffInHours = Math.floor((now - past) / (1000 * 60 * 60));
  
  if (diffInHours < 1) return 'less than 1 hour';
  if (diffInHours < 24) return `${diffInHours} hours`;
  const days = Math.floor(diffInHours / 24);
  return `${days} days`;
};

// Instance Status Badge with animated indicators
const InstanceStatusBadge = ({ status }) => {
  const config = {
    running: {
      label: 'Running',
      dotClass: 'bg-green-500',
      bgClass: 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
      animate: false
    },
    provisioning: {
      label: 'Provisioning',
      dotClass: 'bg-orange-500',
      bgClass: 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400',
      animate: true
    },
    stopped: {
      label: 'Stopped',
      dotClass: 'bg-gray-400',
      bgClass: 'bg-gray-100 dark:bg-gray-800/50 text-gray-700 dark:text-gray-400',
      animate: false
    },
    error: {
      label: 'Error',
      dotClass: 'bg-red-500',
      bgClass: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
      animate: false
    },
    failed: {
      label: 'Failed',
      dotClass: 'bg-red-500',
      bgClass: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
      animate: false
    },
    maintenance: {
      label: 'Maintenance',
      dotClass: 'bg-yellow-500',
      bgClass: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
      animate: true
    }
  };

  const { label, dotClass, bgClass, animate } = config[status] || config.stopped;

  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${bgClass}`}>
      <span className="relative flex h-2 w-2">
        {animate && (
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${dotClass}`}></span>
        )}
        <span className={`relative inline-flex rounded-full h-2 w-2 ${dotClass}`}></span>
      </span>
      {label}
    </span>
  );
};

const ERPInstances = ({ showNotification }) => {
  // State management
  const [loading, setLoading] = useState(true);
  const [instances, setInstances] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateSubdomainModal, setShowUpdateSubdomainModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedInstance, setSelectedInstance] = useState(null);
  const [currentView, setCurrentView] = useState('list'); // 'list' or 'detail'
  const [businessProfile, setBusinessProfile] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const pollIntervalRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Auto-poll when any instance is provisioning
  const hasProvisioningInstances = instances.some(i => i.status === 'provisioning');

  useEffect(() => {
    if (hasProvisioningInstances) {
      pollIntervalRef.current = setInterval(async () => {
        try {
          const response = await axiosInstance.get('/api/erp/instances');
          if (response.data.success) {
            setInstances(response.data.data);
          }
        } catch (error) {
          // Silent fail for background polling
        }
      }, 5000); // Poll every 5 seconds
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [hasProvisioningInstances]);

  const fetchData = async () => {
    await Promise.all([
      fetchInstances(),
      fetchBusinessProfile()
    ]);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await axiosInstance.get('/api/erp/instances');
      if (response.data.success) {
        setInstances(response.data.data);
      }
    } catch (error) {
      showNotification?.('Failed to refresh instances', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const fetchInstances = async () => {
    try {
      const response = await axiosInstance.get('/api/erp/instances');
      if (response.data.success) {
        setInstances(response.data.data);
      } else {
        logger.error('Failed to fetch instances:', response.data.message);
        setInstances([]);
      }
    } catch (error) {
      logger.error('Error fetching instances:', error);
      // Handle different error cases gracefully
      if (error.response?.status === 404 || error.response?.status === 401) {
        setInstances([]);
      } else {
        showNotification?.('Failed to load instances', 'error');
        setInstances([]);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessProfile = async () => {
    try {
      const response = await axiosInstance.get('/api/business/profile');
      if (response.data.success) {
        setBusinessProfile(response.data.data);
      }
    } catch (error) {
      logger.warn('Business profile not found, using fallback data');
      // Set fallback business data
      setBusinessProfile({
        businessName: 'Demo Company',
        subscription: {
          plan: 'Professional',
          status: 'active'
        }
      });
    }
  };

  // Instance action handlers
  const handleInstanceAction = async (instanceId, action) => {
    try {
      setLoading(true);
      // API call would go here - simulating for now
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setInstances(prev => prev.map(instance => 
        (instance._id || instance.id) === instanceId 
          ? { ...instance, status: action === 'start' ? 'running' : 'stopped' }
          : instance
      ));
      
      showNotification?.(`Instance ${action === 'start' ? 'started' : 'stopped'} successfully`, 'success');
    } catch (error) {
      showNotification?.(`Failed to ${action} instance`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEditInstance = (instance) => {
    setSelectedInstance(instance);
    // Edit modal would open here
    showNotification?.('Edit functionality coming soon', 'info');
  };

  const handleDeleteInstance = (instanceId) => {
    const instance = instances.find(inst => inst.id === instanceId || inst._id === instanceId);
    if (instance) {
      setSelectedInstance(instance);
      setShowDeleteConfirmModal(true);
    }
  };

  const confirmDeleteInstance = async (instanceId) => {
    try {
      const response = await axiosInstance.delete(`/api/erp/instances/${instanceId}`);
      
      if (response.data.success) {
        setInstances(prev => prev.filter(instance => 
          instance.id !== instanceId && instance._id !== instanceId
        ));
        showNotification?.('Instance deleted successfully', 'success');
        
        // If we're viewing the deleted instance, go back to list
        if (selectedInstance && (selectedInstance.id === instanceId || selectedInstance._id === instanceId)) {
          setCurrentView('list');
          setSelectedInstance(null);
        }
      } else {
        showNotification?.(response.data.message || 'Failed to delete instance', 'error');
      }
    } catch (error) {
      logger.error('Error deleting instance:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete instance';
      showNotification?.(errorMessage, 'error');
    } finally {
      setShowDeleteConfirmModal(false);
      setSelectedInstance(null);
    }
  };

  const filteredInstances = instances.filter(instance =>
    (instance.subdomain || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (instance.displayName || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
    (instance.fullUrl || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedInstance(null);
  };

  const handleModuleToggle = async (instanceId, moduleId) => {
    try {
      const response = await axiosInstance.post(
        `/api/erp/instances/${instanceId}/modules/${moduleId}/toggle`
      );
      
      if (response.data.success) {
        // Update the selected instance and instances list
        const updatedInstance = response.data.data;
        setSelectedInstance(updatedInstance);
        setInstances(prev => prev.map(instance => 
          (instance._id || instance.id) === instanceId ? updatedInstance : instance
        ));
        showNotification?.(`Module ${moduleId} ${updatedInstance.modules?.includes(moduleId) ? 'enabled' : 'disabled'} successfully`, 'success');
      } else {
        showNotification?.(response.data.message || 'Failed to toggle module', 'error');
      }
    } catch (error) {
      logger.error('Error toggling module:', error);
      showNotification?.(
        error.response?.data?.message || 'Failed to toggle module', 
        'error'
      );
    }
  };

  if (loading && instances.length === 0) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-[#111111] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-orange-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading ERP Instances...</p>
        </div>
      </div>
    );
  }

  // Render detail view if an instance is selected
  if (currentView === 'detail' && selectedInstance) {
    return (
      <>
        <InstanceDetailView 
          instance={selectedInstance} 
          businessProfile={businessProfile}
          onBack={handleBackToList}
          onAction={handleInstanceAction}
          onEdit={() => {/* TODO: Implement edit */}}
          onDelete={handleDeleteInstance}
          showNotification={showNotification}
          onEditSubdomain={() => {

            setShowUpdateSubdomainModal(true);
          }}
          onModuleToggle={handleModuleToggle}
        />
        
        {/* Update Subdomain Modal - Also render in detail view */}
        {showUpdateSubdomainModal && selectedInstance && (
          <UpdateSubdomainModal 
            isOpen={showUpdateSubdomainModal}
            onClose={() => {
              setShowUpdateSubdomainModal(false);
            }}
            showNotification={showNotification}
            instance={selectedInstance}
            onSubdomainUpdated={fetchInstances}
          />
        )}

        {/* Delete Confirmation Modal - Also render in detail view */}
        {showDeleteConfirmModal && selectedInstance && (
          <DeleteConfirmModal 
            isOpen={showDeleteConfirmModal}
            onClose={() => {
              setShowDeleteConfirmModal(false);
              setSelectedInstance(null);
            }}
            instance={selectedInstance}
            onConfirmDelete={confirmDeleteInstance}
          />
        )}
      </>
    );
  }

  return (
    <div className="h-screen bg-gray-50 dark:bg-[#111111] flex flex-col">
      {/* Fixed Header */}
      <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 flex-shrink-0 px-6 py-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ServerIconSolid className="w-4 h-4 text-orange-600 dark:text-orange-400" />
            <h1 className="text-sm font-semibold text-gray-900 dark:text-white">ERP Instances</h1>
          </div>
          <div className="flex items-center gap-2">
            {hasProvisioningInstances && (
              <div className="text-[11px] bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-400 px-1.5 py-0.5 rounded inline-flex items-center gap-1">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-orange-500"></span>
                </span>
                {instances.filter(i => i.status === 'provisioning').length} Provisioning
              </div>
            )}
            <span className="text-[11px] bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-400 px-1.5 py-0.5 rounded">
              {instances.filter(i => i.status === 'running').length} Running
            </span>
            <span className="text-[11px] bg-gray-100 dark:bg-[#111111]/30 text-gray-800 dark:text-gray-400 px-1.5 py-0.5 rounded">
              {instances.length} Total
            </span>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="p-1 text-gray-500 dark:text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-gray-100 dark:hover:bg-[#222222] rounded-md transition-colors disabled:opacity-50"
              title="Refresh instances"
            >
              <ArrowPathIcon className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0">
        <div className="max-w-7xl mx-auto px-6 pt-4 pb-8 space-y-4">
          {/* Instances Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">All Instances</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Create and manage your ERP environments across different stages
              </p>
            </div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              icon={<PlusIcon />}
            >
              New Instance
            </Button>
          </div>

          {/* Instance Search */}
          <div className="max-w-md">
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search instances..."
            />
          </div>

          {/* Instances Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredInstances.map((instance) => (
              <Card key={instance._id || instance.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 bg-gradient-to-br ${getEnvironmentColor(instance.environment)} rounded-lg flex items-center justify-center`}>
                      <ServerIcon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">{instance.displayName}</h3>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{instance.subdomain}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <InstanceStatusBadge status={instance.status} />
                    <ActionMenu
                      options={[
                        { 
                          label: 'View Details', 
                          icon: EyeIcon,
                          onClick: () => {
                            setSelectedInstance(instance);
                            setCurrentView('detail');
                          }
                        },
                        { 
                          label: 'Open Portal', 
                          icon: ArrowTopRightOnSquareIcon,
                          onClick: () => window.open(getInstanceUrl(instance, { withToken: true }), '_blank')
                        },
                        { 
                          label: instance.status === 'running' ? 'Stop' : 'Start',
                          icon: instance.status === 'running' ? PauseIcon : PlayIcon,
                          onClick: () => handleInstanceAction(instance._id || instance.id, instance.status === 'running' ? 'stop' : 'start')
                        },
                        { 
                          label: 'Edit', 
                          icon: PencilIcon,
                          onClick: () => handleEditInstance(instance)
                        },
                        { 
                          label: 'Delete', 
                          icon: TrashIcon,
                          onClick: () => handleDeleteInstance(instance._id || instance.id),
                          destructive: true
                        }
                      ]}
                    />
                  </div>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Environment:</span>
                    <span className={`px-2 py-1 rounded text-xs font-medium ${getEnvironmentBadge(instance.environment)}`}>
                      {instance.environment}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Region:</span>
                    <span className="text-gray-900 dark:text-white">{instance.region}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">URL:</span>
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); window.open(getInstanceUrl(instance, { withToken: true }), '_blank'); }}
                      className="text-orange-600 dark:text-orange-400 hover:underline truncate max-w-32"
                    >
                      {getInstanceDisplayUrl(instance)}
                    </a>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Modules:</span>
                    <span className="text-gray-900 dark:text-white">{instance.modules?.length ?? 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Created:</span>
                    <span className="text-gray-900 dark:text-white">{new Date(instance.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>

                {instance.status === 'provisioning' && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-2 text-xs text-orange-600 dark:text-orange-400">
                      <div className="w-3.5 h-3.5 border-2 border-orange-300 border-t-orange-600 rounded-full animate-spin"></div>
                      <span>{instance.provisioningStep || 'Setting up your instance...'}</span>
                    </div>
                    {instance.provisioningProgress != null && (
                      <div className="mt-2 w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                        <div
                          className="bg-orange-500 h-1.5 rounded-full transition-all duration-500"
                          style={{ width: `${instance.provisioningProgress || 0}%` }}
                        ></div>
                      </div>
                    )}
                  </div>
                )}

                {(instance.status === 'error' || instance.status === 'failed') && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                    <div className="flex items-center gap-2 text-xs text-red-600 dark:text-red-400">
                      <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                      <span>{instance.errorMessage || 'Provisioning failed'}</span>
                    </div>
                  </div>
                )}

                {instance.status === 'running' && (
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                    <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                      <span>Last Active:</span>
                      <span>{formatDistanceToNow(instance.lastActiveAt)} ago</span>
                    </div>
                  </div>
                )}

                {/* Manage Button */}
                <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-800">
                  <button
                    onClick={() => {
                      setSelectedInstance(instance);
                      setCurrentView('detail');
                    }}
                    className="flex items-center bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
                  >
                    <CogIcon className="w-3.5 h-3.5 mr-1.5" />
                    Manage
                  </button>
                </div>
              </Card>
            ))}
          </div>

          {/* Empty State */}
          {instances.length === 0 && (
            <div className="text-center py-12">
              <ServerIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">No instances yet</h3>
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Create your first ERP instance to get started with your business applications.
              </p>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                icon={<PlusIcon />}
              >
                Create Instance
              </Button>
            </div>
          )}

          {/* No Search Results */}
          {filteredInstances.length === 0 && instances.length > 0 && searchQuery && (
            <div className="text-center py-8">
              <ServerIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-600 dark:text-gray-400">
                No instances match "{searchQuery}"
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Create Instance Modal */}
      {showCreateModal && (
        <CreateInstanceModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          showNotification={showNotification}
          onInstanceCreated={(newInstance) => {
            // Optimistic update: add instance with provisioning status immediately
            if (newInstance) {
              setInstances(prev => [newInstance, ...prev]);
            } else {
              fetchInstances();
            }
          }}
        />
      )}

      {/* Update Subdomain Modal */}
      {showUpdateSubdomainModal && selectedInstance && (
        <UpdateSubdomainModal 
          isOpen={showUpdateSubdomainModal}
          onClose={() => {
            setShowUpdateSubdomainModal(false);
            setSelectedInstance(null);
          }}
          showNotification={showNotification}
          instance={selectedInstance}
          onSubdomainUpdated={fetchInstances}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmModal && selectedInstance && (
        <DeleteConfirmModal 
          isOpen={showDeleteConfirmModal}
          onClose={() => {
            setShowDeleteConfirmModal(false);
            setSelectedInstance(null);
          }}
          instance={selectedInstance}
          onConfirmDelete={confirmDeleteInstance}
        />
      )}
    </div>
  );
};

// Create Instance Modal Component
const CreateInstanceModal = ({ isOpen, onClose, showNotification, onInstanceCreated }) => {
  const [formData, setFormData] = useState({
    displayName: '',
    subdomain: '',
    environment: 'production',
    modules: []
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [errors, setErrors] = useState({});

  const availableModules = [
    { id: 'financial', label: 'Financial Management' },
    { id: 'sales', label: 'Sales' },
    { id: 'crm', label: 'CRM' },
    { id: 'inventory', label: 'Inventory Management' },
    { id: 'hr', label: 'Human Resources' },
    { id: 'procurement', label: 'Procurement' },
    { id: 'manufacturing', label: 'Manufacturing' },
    { id: 'projects', label: 'Project Management' },
    { id: 'pos', label: 'Point of Sale' },
    { id: 'reports', label: 'Reports' },
    { id: 'restaurant', label: 'Restaurant' },
    { id: 'sms', label: 'School Management' }
  ];

  // Real-time availability check
  const checkAvailability = async (subdomain) => {
    if (!subdomain) {
      setAvailabilityStatus(null);
      return;
    }
    
    if (subdomain.length < 3) {
      setAvailabilityStatus({ available: false, message: 'Too short (min 3 characters)' });
      return;
    }
    
    setIsChecking(true);
    try {
      const response = await axiosInstance.get(`/api/erp/instances/check-subdomain/${subdomain}`);
      setAvailabilityStatus({
        available: response.data.available,
        message: response.data.message
      });
    } catch (error) {
      setAvailabilityStatus({ available: false, message: 'Error checking availability' });
    } finally {
      setIsChecking(false);
    }
  };

  // Debounced availability check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.subdomain) {
        checkAvailability(formData.subdomain);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.subdomain]);

  const handleModuleToggle = (moduleId) => {
    setFormData(prev => ({
      ...prev,
      modules: prev.modules.includes(moduleId) 
        ? prev.modules.filter(id => id !== moduleId)
        : [...prev.modules, moduleId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.displayName.trim()) {
      newErrors.displayName = 'Display name is required';
    }
    
    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain = 'Subdomain must contain only lowercase letters, numbers, and hyphens';
    } else if (availabilityStatus && !availabilityStatus.available) {
      newErrors.subdomain = availabilityStatus.message || 'Subdomain is not available';
    }
    
    if (formData.modules.length === 0) {
      newErrors.modules = 'Please select at least one module';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axiosInstance.post('/api/erp/instances', {
        displayName: formData.displayName,
        subdomain: formData.subdomain,
        environment: formData.environment,
        modules: formData.modules
      });
      
      if (response.data.success) {
        showNotification?.('Instance creation started. Provisioning in progress...', 'success');
        // Pass created instance data for optimistic update
        onInstanceCreated?.(response.data.data);
        handleClose();
      } else {
        showNotification?.(response.data.message || 'Failed to create instance', 'error');
      }
    } catch (error) {
      logger.error('Error creating instance:', error);
      // 402 subscription guard returns the reason in `error`; route validation
      // uses `message`. Read both so the real reason is shown, not a generic one.
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        'Failed to create instance';
      showNotification?.(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      displayName: '',
      subdomain: '',
      environment: 'production',
      modules: []
    });
    setErrors({});
    setAvailabilityStatus(null);
    onClose();
  };

  const getAvailabilityIcon = () => {
    if (isChecking) {
      return <div className="w-4 h-4 border-2 border-gray-300 border-t-orange-600 rounded-full animate-spin"></div>;
    }
    if (availabilityStatus?.available) {
      return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
    }
    if (availabilityStatus && !availabilityStatus.available) {
      return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
    }
    return null;
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Create New Instance"
      description="Set up a new ERP instance for your business"
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Display Name
          </label>
          <input
            type="text"
            value={formData.displayName}
            onChange={(e) => setFormData({...formData, displayName: e.target.value})}
            className={`w-full px-3 py-2 border ${errors.displayName ? 'border-red-300' : 'border-gray-300 dark:border-gray-700'} bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500`}
            placeholder="Production Main"
            required
          />
          {errors.displayName && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.displayName}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Subdomain
          </label>
          <div className="flex">
            <input
              type="text"
              value={formData.subdomain}
              onChange={(e) => setFormData({...formData, subdomain: e.target.value.toLowerCase()})}
              className={`flex-1 px-3 py-2 border ${errors.subdomain ? 'border-red-300' : 'border-gray-300 dark:border-gray-700'} bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white rounded-l-lg focus:ring-2 focus:ring-orange-500`}
              placeholder="acme-corp"
              required
            />
            <span className="px-3 py-2 bg-gray-50 dark:bg-[#222222] border border-l-0 border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 rounded-r-lg">
              .tellency.com
            </span>
          </div>
          
          {/* Availability Status */}
          {formData.subdomain && (
            <div className="mt-2 flex items-center space-x-2">
              {getAvailabilityIcon()}
              <span className={`text-sm ${
                availabilityStatus?.available 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {availabilityStatus?.available 
                  ? '✓ Available' 
                  : availabilityStatus?.message || 'Checking...'}
              </span>
            </div>
          )}
          
          {errors.subdomain && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.subdomain}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Environment
          </label>
          <select
            value={formData.environment}
            onChange={(e) => setFormData({...formData, environment: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-orange-500"
          >
            <option value="production">Production</option>
            <option value="staging">Staging</option>
            <option value="development">Development</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Select Modules
          </label>
          <div className="grid grid-cols-2 gap-3">
            {availableModules.map((module) => (
              <label key={module.id} className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.modules.includes(module.id)}
                  onChange={() => handleModuleToggle(module.id)}
                  className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                />
                <span className="text-sm text-gray-700 dark:text-gray-300">{module.label}</span>
              </label>
            ))}
          </div>
          {errors.modules && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.modules}</p>
          )}
        </div>

        {isSubmitting && (
          <div className="text-center py-2">
            <div className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating instance...
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
};

// Update Subdomain Modal Component
const UpdateSubdomainModal = ({ isOpen, onClose, showNotification, instance, onSubdomainUpdated }) => {
  const [formData, setFormData] = useState({
    subdomain: instance?.subdomain || ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [availabilityStatus, setAvailabilityStatus] = useState(null);
  const [errors, setErrors] = useState({});
  
  // Real-time availability check
  const checkAvailability = async (subdomain) => {
    if (!subdomain || subdomain === instance?.subdomain) {
      setAvailabilityStatus(null);
      return;
    }
    
    if (subdomain.length < 3) {
      setAvailabilityStatus({ available: false, message: 'Too short (min 3 characters)' });
      return;
    }
    
    setIsChecking(true);
    try {
      const response = await axiosInstance.get(`/api/erp/instances/check-subdomain/${subdomain}`);
      setAvailabilityStatus({
        available: response.data.available,
        message: response.data.message
      });
    } catch (error) {
      setAvailabilityStatus({ available: false, message: 'Error checking availability' });
    } finally {
      setIsChecking(false);
    }
  };

  // Debounced availability check
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (formData.subdomain && formData.subdomain !== instance?.subdomain) {
        checkAvailability(formData.subdomain);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [formData.subdomain, instance?.subdomain]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.subdomain.trim()) {
      newErrors.subdomain = 'Subdomain is required';
    } else if (!/^[a-z0-9-]+$/.test(formData.subdomain)) {
      newErrors.subdomain = 'Subdomain must contain only lowercase letters, numbers, and hyphens';
    } else if (formData.subdomain === instance?.subdomain) {
      newErrors.subdomain = 'New subdomain must be different from current one';
    } else if (availabilityStatus && !availabilityStatus.available) {
      newErrors.subdomain = availabilityStatus.message || 'Subdomain is not available';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await axiosInstance.put(`/api/erp/instances/${instance._id}/subdomain`, {
        subdomain: formData.subdomain
      });
      
      if (response.data.success) {
        showNotification?.('Subdomain updated successfully!', 'success');
        onSubdomainUpdated?.();
        handleClose();
      } else {
        showNotification?.(response.data.message || 'Failed to update subdomain', 'error');
      }
    } catch (error) {
      logger.error('Error updating subdomain:', error);
      const errorMessage = error.response?.data?.message || 'Failed to update subdomain';
      showNotification?.(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ subdomain: instance?.subdomain || '' });
    setAvailabilityStatus(null);
    setErrors({});
    onClose();
  };

  const getAvailabilityIcon = () => {
    if (isChecking) {
      return <div className="w-4 h-4 border-2 border-gray-300 border-t-orange-600 rounded-full animate-spin"></div>;
    }
    if (availabilityStatus?.available) {
      return <CheckCircleIcon className="w-4 h-4 text-green-600" />;
    }
    if (availabilityStatus && !availabilityStatus.available) {
      return <ExclamationTriangleIcon className="w-4 h-4 text-red-600" />;
    }
    return null;
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Update Subdomain"
      description={`Update the subdomain for ${instance?.displayName}`}
      onSubmit={handleSubmit}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Current URL
          </label>
          <div className="p-3 bg-gray-50 dark:bg-[#222222] rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              https://{instance?.subdomain}.tellency.com
            </div>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            New Subdomain
          </label>
          <div className="flex">
            <input
              type="text"
              value={formData.subdomain}
              onChange={(e) => setFormData({...formData, subdomain: e.target.value.toLowerCase()})}
              className={`flex-1 px-3 py-2 border ${errors.subdomain ? 'border-red-300' : 'border-gray-300 dark:border-gray-700'} bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-white rounded-l-lg focus:ring-2 focus:ring-orange-500`}
              placeholder="new-subdomain"
              required
              disabled={isSubmitting}
            />
            <span className="px-3 py-2 bg-gray-50 dark:bg-[#222222] border border-l-0 border-gray-300 dark:border-gray-700 text-gray-500 dark:text-gray-400 rounded-r-lg">
              .tellency.com
            </span>
          </div>
          
          {/* Availability Status */}
          {formData.subdomain && formData.subdomain !== instance?.subdomain && (
            <div className="mt-2 flex items-center space-x-2">
              {getAvailabilityIcon()}
              <span className={`text-sm ${
                availabilityStatus?.available 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {availabilityStatus?.available 
                  ? '✓ Available' 
                  : availabilityStatus?.message || 'Checking...'}
              </span>
            </div>
          )}
          
          {errors.subdomain && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">{errors.subdomain}</p>
          )}
        </div>

        {formData.subdomain && formData.subdomain !== instance?.subdomain && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New URL Preview
            </label>
            <div className="p-3 bg-orange-50 dark:bg-orange-900/20 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="text-sm text-orange-700 dark:text-orange-300">
                https://{formData.subdomain}.tellency.com
              </div>
            </div>
          </div>
        )}

        {isSubmitting && (
          <div className="text-center py-2">
            <div className="inline-flex items-center text-sm text-gray-600 dark:text-gray-400">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Updating subdomain...
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({ isOpen, onClose, instance, onConfirmDelete }) => {
  const [confirmText, setConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  
  const requiredText = instance?.displayName || instance?.subdomain || 'instance';
  const isConfirmValid = confirmText === requiredText;

  const handleConfirmDelete = async () => {
    if (!isConfirmValid) return;
    
    setIsDeleting(true);
    try {
      await onConfirmDelete(instance._id || instance.id);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setConfirmText('');
    onClose();
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Delete Instance"
      description="This action cannot be undone. All data associated with this instance will be permanently deleted."
      onSubmit={handleConfirmDelete}
      submitDisabled={!isConfirmValid || isDeleting}
      submitLabel={isDeleting ? 'Deleting...' : 'Delete Instance'}
      submitClassName="bg-red-600 hover:bg-red-700 disabled:bg-red-400"
    >
      <div className="space-y-6">
        {/* Warning Section */}
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-start space-x-3">
            <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="text-sm font-semibold text-red-800 dark:text-red-200 mb-2">
                Warning: This action is irreversible
              </h4>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1">
                <li>• All databases and data will be permanently deleted</li>
                <li>• User access will be immediately revoked</li>
                <li>• The subdomain will become available for reuse</li>
                <li>• All configurations and settings will be lost</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Instance Details */}
        <div className="bg-gray-50 dark:bg-[#1a1a1a] rounded-lg p-4 border border-gray-200 dark:border-gray-800">
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">
            Instance to be deleted:
          </h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Name:</span>
              <span className="font-medium text-gray-900 dark:text-white">{instance?.displayName}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">URL:</span>
              <span className="font-medium text-gray-900 dark:text-white">
                {getInstanceDisplayUrl(instance)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600 dark:text-gray-400">Environment:</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                instance?.environment === 'production' 
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                  : instance?.environment === 'staging'
                  ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                  : 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
              }`}>
                {instance?.environment}
              </span>
            </div>
          </div>
        </div>

        {/* Confirmation Input */}
        <div>
          <label className="block text-sm font-medium text-gray-900 dark:text-white mb-2">
            To confirm deletion, type "{requiredText}" below:
          </label>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-red-500 ${
              confirmText && !isConfirmValid
                ? 'border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-600'
                : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-[#2a2a2a]'
            } text-gray-900 dark:text-white`}
            placeholder={`Type "${requiredText}" to confirm`}
            disabled={isDeleting}
            autoComplete="off"
          />
          {confirmText && !isConfirmValid && (
            <p className="mt-1 text-sm text-red-600 dark:text-red-400">
              Text must match exactly: "{requiredText}"
            </p>
          )}
          {isConfirmValid && (
            <p className="mt-1 text-sm text-green-600 dark:text-green-400">
              ✓ Confirmation text matches
            </p>
          )}
        </div>

        {/* Final Warning */}
        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            This action will permanently delete the instance and all associated data.
            <br />
            <strong className="text-red-600 dark:text-red-400">This cannot be undone.</strong>
          </p>
        </div>

        {isDeleting && (
          <div className="text-center py-2">
            <div className="inline-flex items-center text-sm text-red-600 dark:text-red-400">
              <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Deleting instance...
            </div>
          </div>
        )}
      </div>
    </FormModal>
  );
};

// Instance Detail View Component  
const InstanceDetailView = ({ instance, businessProfile, onBack, onAction, onEdit, onDelete, showNotification, onEditSubdomain, onModuleToggle }) => {
  if (!instance) return null;

  const getStatusColor = (status) => {
    const colors = {
      running: 'text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400',
      stopped: 'text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400',
      starting: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400'
    };
    return colors[status] || 'text-gray-600 bg-gray-100 dark:bg-[#111111]/30 dark:text-gray-400';
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 80) return 'bg-red-500';
    if (percentage >= 60) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showNotification?.('Copied to clipboard', 'success');
  };

  return (
    <div className="h-screen bg-gray-50 dark:bg-[#111111] flex flex-col">
      {/* Fixed Header */}
      <div className="bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 dark:bg-[#222222] hover:bg-gray-200 dark:hover:bg-[#333333] transition-colors text-gray-600 dark:text-gray-300"
              >
                <ArrowLeftIcon className="w-5 h-5" />
              </button>

              <div className="flex items-center space-x-3">
                <div className={`w-12 h-12 bg-gradient-to-br ${getEnvironmentColor(instance.environment)} rounded-xl flex items-center justify-center shadow-lg`}>
                  <ServerIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {instance.displayName || instance.subdomain}
                  </h1>
                  <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
                    <span>{businessProfile?.businessName || 'Business'}</span>
                    <span>•</span>
                    <span>{businessProfile?.subscription?.plan || 'Professional'} Plan</span>
                    <span>•</span>
                    <span className="capitalize">{instance.environment}</span>
                    <span>•</span>
                    <InstanceStatusBadge status={instance.status} />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons - Opposite to instance name */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => window.open(getInstanceUrl(instance, { withToken: true }), '_blank')}
                className="flex items-center bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              >
                <ArrowTopRightOnSquareIcon className="w-3.5 h-3.5 mr-1.5" />
                Open Portal
              </button>
              <button
                onClick={() => instance.managementUrls?.admin && window.open(instance.managementUrls.admin, '_blank')}
                className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              >
                <CogIcon className="w-3.5 h-3.5 mr-1.5" />
                Admin Panel
              </button>
              <button
                onClick={() => onDelete(instance._id || instance.id)}
                className="flex items-center bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition-colors"
              >
                <TrashIcon className="w-3.5 h-3.5 mr-1.5" />
                Delete Instance
              </button>
            </div>
          </div>

          <BreadcrumbNavigation />
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto min-h-0 bg-gray-50 dark:bg-[#111111]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {/* Basic Information */}
            <Card className="p-5 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center mr-2.5 shadow-sm">
                    <CogIcon className="w-4 h-4 text-white" />
                  </div>
                  Basic Information
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Database</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{instance.capacity?.database?.name}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Version</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{instance.version}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Uptime</span>
                  <span className="text-sm font-semibold text-green-600 dark:text-green-400">{instance.performance?.uptime}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Created</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {new Date(instance.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="py-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide block mb-2">URL</span>
                  <div className="flex items-center justify-between gap-2">
                    <a
                      href="#"
                      onClick={(e) => { e.preventDefault(); window.open(getInstanceUrl(instance, { withToken: true }), '_blank'); }}
                      className="text-sm font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors truncate flex-1 min-w-0"
                      title={getInstanceDisplayUrl(instance)}
                    >
                      {getInstanceDisplayUrl(instance)}
                    </a>
                    <div className="flex items-center space-x-1 flex-shrink-0">
                      <button
                        onClick={() => copyToClipboard(getInstanceUrl(instance))}
                        className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222] rounded transition-colors"
                        title="Copy URL"
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={onEditSubdomain}
                        className="p-1.5 text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
                        title="Edit Subdomain"
                      >
                        <PencilIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Performance Metrics */}
            <Card className="p-5 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center mr-2.5 shadow-sm">
                    <ChartBarIcon className="w-4 h-4 text-white" />
                  </div>
                  Performance
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">DB Response</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {instance.performance?.dbResponseTime}ms
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">API Response</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {instance.performance?.apiResponseTime}ms
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Current Users</span>
                  <span className="text-sm font-semibold text-blue-600 dark:text-blue-400">
                    {instance.performance?.currentUsers}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Peak Users</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {instance.performance?.peakUsers}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Error Rate</span>
                  <span className={`text-sm font-semibold ${instance.performance?.errorRate > 0.01 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                    {(instance.performance?.errorRate * 100).toFixed(2)}%
                  </span>
                </div>
              </div>
            </Card>

            {/* Server Health */}
            <Card className="p-5 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg flex items-center justify-center mr-2.5 shadow-sm">
                    <CpuChipIcon className="w-4 h-4 text-white" />
                  </div>
                  Server Health
                </h3>
              </div>
              <div className="space-y-4">
                {/* CPU */}
                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">CPU ({instance.serverHealth?.cpu?.cores} cores)</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {instance.serverHealth?.cpu?.usage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-[#333333] rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${getUsageColor(instance.serverHealth?.cpu?.usage || 0)}`}
                      style={{ width: `${instance.serverHealth?.cpu?.usage || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Memory */}
                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400">Memory</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {instance.serverHealth?.memory?.used} / {instance.serverHealth?.memory?.total} {instance.serverHealth?.memory?.unit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-[#333333] rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${getUsageColor((instance.serverHealth?.memory?.used / instance.serverHealth?.memory?.total) * 100 || 0)}`}
                      style={{ width: `${(instance.serverHealth?.memory?.used / instance.serverHealth?.memory?.total) * 100 || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Storage Usage */}
            <Card className="p-5 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-lg flex items-center justify-center mr-2.5 shadow-sm">
                    <CircleStackIcon className="w-4 h-4 text-white" />
                  </div>
                  Storage Usage
                </h3>
              </div>
              <div className="space-y-4">
                {/* Database */}
                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block">Database</span>
                      <span className="text-xs text-gray-500 dark:text-gray-500">{instance.capacity?.database?.tables} tables, {instance.capacity?.database?.records} records</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {instance.capacity?.database?.used} / {instance.capacity?.database?.quota} {instance.capacity?.database?.unit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-[#333333] rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${getUsageColor((instance.capacity?.database?.used / instance.capacity?.database?.quota) * 100 || 0)}`}
                      style={{ width: `${(instance.capacity?.database?.used / instance.capacity?.database?.quota) * 100 || 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* File Storage */}
                <div className="bg-gray-50 dark:bg-[#222222] rounded-lg p-3">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="text-xs font-medium text-gray-600 dark:text-gray-400 block">File Storage</span>
                      <span className="text-xs text-gray-500 dark:text-gray-500">{instance.capacity?.storage?.files} files, {instance.capacity?.storage?.attachments} attachments</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {instance.capacity?.storage?.used} / {instance.capacity?.storage?.quota} {instance.capacity?.storage?.unit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-[#333333] rounded-full h-2.5 overflow-hidden">
                    <div
                      className={`h-2.5 rounded-full transition-all duration-300 ${getUsageColor((instance.capacity?.storage?.used / instance.capacity?.storage?.quota) * 100 || 0)}`}
                      style={{ width: `${(instance.capacity?.storage?.used / instance.capacity?.storage?.quota) * 100 || 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Module Management */}
            <Card className="p-5 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow lg:col-span-2 xl:col-span-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center mr-2.5 shadow-sm">
                    <PuzzlePieceIcon className="w-4 h-4 text-white" />
                  </div>
                  Module Management
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {(instance.modules || []).length} active
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3">
                {[
                  { id: 'financial', label: 'Financial', gradient: 'from-emerald-500 to-green-600' },
                  { id: 'sales', label: 'Sales', gradient: 'from-blue-500 to-blue-600' },
                  { id: 'crm', label: 'CRM', gradient: 'from-purple-500 to-violet-600' },
                  { id: 'inventory', label: 'Inventory', gradient: 'from-orange-500 to-amber-600' },
                  { id: 'hr', label: 'HR', gradient: 'from-pink-500 to-rose-600' },
                  { id: 'procurement', label: 'Procurement', gradient: 'from-yellow-500 to-orange-600' },
                  { id: 'manufacturing', label: 'Manufacturing', gradient: 'from-gray-500 to-slate-600' },
                  { id: 'projects', label: 'Projects', gradient: 'from-teal-500 to-cyan-600' },
                  { id: 'pos', label: 'Point of Sale', gradient: 'from-indigo-500 to-blue-600' },
                  { id: 'reports', label: 'Reports', gradient: 'from-red-500 to-pink-600' },
                  { id: 'restaurant', label: 'Restaurant', gradient: 'from-amber-500 to-orange-600' },
                  { id: 'sms', label: 'School Mgmt', gradient: 'from-sky-500 to-blue-600' }
                ].map(mod => {
                  const isEnabled = (instance.modules || []).includes(mod.id);
                  return (
                    <button
                      key={mod.id}
                      onClick={() => onModuleToggle(instance._id || instance.id, mod.id)}
                      className={`relative p-3 rounded-xl border-2 transition-all duration-200 text-left group ${
                        isEnabled
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 shadow-sm'
                          : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#222222] hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1.5">
                        <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${mod.gradient} flex items-center justify-center shadow-sm ${!isEnabled ? 'opacity-40' : ''}`}>
                          <PuzzlePieceIcon className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                          isEnabled
                            ? 'bg-orange-500 text-white'
                            : 'bg-gray-200 dark:bg-gray-600'
                        }`}>
                          {isEnabled && <CheckCircleIcon className="w-4 h-4" />}
                        </div>
                      </div>
                      <span className={`text-xs font-medium block ${
                        isEnabled
                          ? 'text-gray-900 dark:text-white'
                          : 'text-gray-500 dark:text-gray-400'
                      }`}>
                        {mod.label}
                      </span>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Management URLs */}
            <Card className="p-5 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-lg flex items-center justify-center mr-2.5 shadow-sm">
                    <LinkIcon className="w-4 h-4 text-white" />
                  </div>
                  Management URLs
                </h3>
              </div>
              <div className="space-y-2">
                {Object.entries(instance.managementUrls || {}).map(([key, url]) => (
                  <div key={key} className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-[#222222] rounded-lg hover:bg-gray-100 dark:hover:bg-[#2a2a2a] transition-colors">
                    <span className="text-xs font-medium text-gray-600 dark:text-gray-400 capitalize">{key}</span>
                    <div className="flex items-center space-x-1.5">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs font-medium text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300"
                      >
                        Open
                      </a>
                      <button
                        onClick={() => copyToClipboard(url)}
                        className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-200 dark:hover:bg-[#222222] rounded transition-colors"
                      >
                        <DocumentDuplicateIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </Card>

            {/* Backup Status */}
            <Card className="p-5 bg-white dark:bg-[#1a1a1a] border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center">
                  <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg flex items-center justify-center mr-2.5 shadow-sm">
                    <ClockIcon className="w-4 h-4 text-white" />
                  </div>
                  Backup Status
                </h3>
              </div>
              <div className="space-y-3">
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Last Backup</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">
                    {instance.backup?.lastBackup
                      ? `${formatDistanceToNow(new Date(instance.backup.lastBackup))} ago`
                      : 'No backups yet'}
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-800">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Frequency</span>
                  <span className="text-sm font-semibold text-gray-900 dark:text-white">{instance.backup?.frequency || '—'}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">Status</span>
                  <div className="flex items-center space-x-1.5">
                    {instance.backup?.status === 'healthy' ? (
                      <>
                        <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        <span className="text-sm font-semibold text-green-600 dark:text-green-400 capitalize">
                          {instance.backup.status}
                        </span>
                      </>
                    ) : (
                      <>
                        <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500" />
                        <span className="text-sm font-semibold text-yellow-600 dark:text-yellow-400 capitalize">
                          {instance.backup?.status || 'Unknown'}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ERPInstances;